if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = null;
        this.boundSubmitHandler = this.onSubmitHandler.bind(this);
        this.cart = null;
        this.submitButton = null;

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        this.setupForm();
        this.cart = this.getCart();
        if (this.submitButton && document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }
      }

      setupForm() {
        const form = this.querySelector('form');
        if (!form || this.form === form) return;

        if (this.form) {
          this.form.removeEventListener('submit', this.boundSubmitHandler);
        }

        this.form = form;
        const variantInput = this.form.querySelector('[name="id"]');
        if (variantInput) variantInput.disabled = false;
        this.form.addEventListener('submit', this.boundSubmitHandler);
        this.submitButton = this.querySelector('[type="submit"]');
      }

      getCart() {
        return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      }

      getFallbackSectionIds() {
        return ['cart-drawer', 'cart-icon-bubble', 'cart-notification-product', 'cart-notification-button'];
      }

      renderFallbackSections(response, { open: openCartUi = true } = {}) {
        if (!response || !response.sections) return;

        const parser = new DOMParser();
        const bubbleElement = document.getElementById('cart-icon-bubble');
        const bubbleMarkup = response.sections['cart-icon-bubble'];
        if (bubbleElement && bubbleMarkup) {
          const bubbleDocument = parser.parseFromString(bubbleMarkup, 'text/html');
          const bubbleSource = bubbleDocument.querySelector('#cart-icon-bubble') || bubbleDocument.querySelector('.shopify-section');
          if (bubbleSource) {
            bubbleElement.innerHTML = bubbleSource.innerHTML;
          }
        }

        const drawerElement = document.querySelector('cart-drawer');
        const drawerMarkup = response.sections['cart-drawer'];
        if (drawerElement && drawerMarkup) {
          const drawerContainer = document.querySelector('#CartDrawer');
          if (drawerContainer) {
            const drawerDocument = parser.parseFromString(drawerMarkup, 'text/html');
            const drawerSource = drawerDocument.querySelector('#CartDrawer') || drawerDocument.querySelector('.drawer__inner');
            if (drawerSource) {
              drawerContainer.innerHTML = drawerSource.innerHTML;
            }
            const overlay = drawerElement.querySelector('#CartDrawer-Overlay');
            if (overlay) {
              overlay.addEventListener('click', drawerElement.close.bind(drawerElement));
            }
            if (openCartUi && typeof drawerElement.open === 'function') {
              drawerElement.open();
            }
          }
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        this.cart = this.getCart();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (this.submitButton.dataset.loginRequired === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        let sectionsToRender = this.getFallbackSectionIds();
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          sectionsToRender = this.cart.getSectionsToRender().map((section) => section.id);
        }
        formData.append('sections', sectionsToRender);
        formData.append('sections_url', window.location.pathname);

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const openCartUi = this.dataset.openCartOnAdd !== 'false';
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    if (this.cart && typeof this.cart.renderContents === 'function') {
                      this.cart.renderContents(response, { open: openCartUi });
                    } else {
                      this.renderFallbackSections(response, { open: openCartUi });
                    }
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              if (this.cart && typeof this.cart.renderContents === 'function') {
                this.cart.renderContents(response, { open: openCartUi });
              } else {
                this.renderFallbackSections(response, { open: openCartUi });
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}

if (!window.__productFormSubmitGuardAttached) {
  window.__productFormSubmitGuardAttached = true;
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.dataset.type !== 'add-to-cart-form') return;

      const productForm = form.closest('product-form');
      if (!productForm) return;
      if (typeof productForm.onSubmitHandler !== 'function') return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (typeof productForm.setupForm === 'function') {
        productForm.setupForm();
      }
      productForm.onSubmitHandler(event);
    },
    true
  );
}
